import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardShell from '@/components/DashboardShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  ChevronLeft, Send, Shield, AlertTriangle, MessageCircle, 
  Headphones, Briefcase, Lock, Check, CheckCheck, Clock,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * MessagesPage - Uber/Airbnb style messaging
 * Fixed: Stable input, no auto-scroll issues
 */
const MessagesPage = () => {
  const { threadId } = useParams();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job');
  const navigate = useNavigate();
  const { user, token } = useAuth();

  // View mode
  const [view, setView] = useState(threadId || jobId ? 'thread' : 'list');
  
  // Thread list state
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  
  // Active thread state
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // Message input - using ref to prevent focus loss
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);
  
  // New support conversation
  const [showNewSupport, setShowNewSupport] = useState(searchParams.get('new') === 'support');
  const [supportMessage, setSupportMessage] = useState('');
  
  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const isUserScrollingRef = useRef(false);

  // Axios config
  const getAxiosConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // Scroll to bottom only for NEW messages
  const scrollToBottomIfNeeded = useCallback(() => {
    if (!messagesContainerRef.current || isUserScrollingRef.current) return;
    
    const container = messagesContainerRef.current;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    // Only auto-scroll if user is near bottom or new messages arrived
    if (isNearBottom || messages.length > lastMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    lastMessageCountRef.current = messages.length;
  }, [messages.length]);

  // Load threads list
  const loadThreads = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/messaging/threads`, getAxiosConfig());
      setThreads(res.data);
    } catch (error) {
      console.error('Failed to load threads:', error);
    } finally {
      setLoadingThreads(false);
    }
  }, [token, getAxiosConfig]);

  // Load specific thread
  const loadThread = useCallback(async (tId) => {
    if (!token || !tId) return;
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${API_URL}/messaging/threads/${tId}`, getAxiosConfig());
      setActiveThread(res.data.thread);
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error('Failed to load thread:', error);
      toast.error('Failed to load conversation');
    } finally {
      setLoadingMessages(false);
    }
  }, [token, getAxiosConfig]);

  // Initial load only
  useEffect(() => {
    loadThreads();
    
    if (threadId) {
      setView('thread');
      loadThread(threadId);
    }
  }, [threadId]); // Only run on mount or threadId change

  // Scroll effect - only when message count changes
  useEffect(() => {
    scrollToBottomIfNeeded();
  }, [messages.length]);

  // Handle user scroll to prevent auto-scroll interruption
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const container = messagesContainerRef.current;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    isUserScrollingRef.current = !isNearBottom;
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    const messageText = newMessage.trim();
    setSending(true);
    
    // Optimistic update - add message immediately
    const tempMessage = {
      id: `temp-${Date.now()}`,
      content: messageText,
      sender_id: user?.id,
      sender_name: user?.name,
      sender_type: 'user',
      created_at: new Date().toISOString(),
      read_by: [user?.id],
      _pending: true
    };
    
    setMessages(prev => [...prev, tempMessage]);
    setNewMessage('');
    
    // Keep focus on input
    setTimeout(() => inputRef.current?.focus(), 0);
    
    try {
      const endpoint = activeThread?.id 
        ? `${API_URL}/messaging/threads/${activeThread.id}/messages`
        : `${API_URL}/messages`;
      
      const payload = activeThread?.id 
        ? { content: messageText }
        : { job_id: activeThread?.job_id, content: messageText, receiver_id: activeThread?.other_participant_id };
      
      const res = await axios.post(endpoint, payload, getAxiosConfig());
      
      // Replace temp message with real one
      setMessages(prev => prev.map(m => 
        m.id === tempMessage.id ? { ...res.data, _pending: false } : m
      ));
      
    } catch (error) {
      // Remove temp message on error
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      toast.error(error.response?.data?.detail || 'Failed to send message');
      setNewMessage(messageText); // Restore message
    } finally {
      setSending(false);
    }
  };

  // Start new support conversation
  const handleNewSupport = async (e) => {
    e.preventDefault();
    if (!supportMessage.trim()) return;
    
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/messaging/support/new`, {
        message: supportMessage.trim()
      }, getAxiosConfig());
      
      toast.success('Support conversation started');
      setShowNewSupport(false);
      setSupportMessage('');
      
      // Navigate to the new thread
      if (res.data.thread_id) {
        navigate(`/messages/${res.data.thread_id}`);
      } else {
        loadThreads();
      }
    } catch (error) {
      toast.error('Failed to start support conversation');
    } finally {
      setSending(false);
    }
  };

  // Open thread
  const openThread = (thread) => {
    navigate(`/messages/${thread.id}`);
  };

  // ============================================================
  // THREAD LIST VIEW
  // ============================================================
  const ThreadListView = () => (
    <DashboardShell>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-zinc-900">Messages</h1>
          <Button
            onClick={() => setShowNewSupport(true)}
            variant="outline"
            size="sm"
            className="gap-2"
            data-testid="new-support-btn"
          >
            <Headphones className="w-4 h-4" />
            Contact Support
          </Button>
        </div>

        {/* New Support Modal */}
        <AnimatePresence>
          {showNewSupport && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowNewSupport(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-xl p-6 w-full max-w-md"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold">Contact Support</h2>
                  <button onClick={() => setShowNewSupport(false)} className="text-zinc-400 hover:text-zinc-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleNewSupport}>
                  <Textarea
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    placeholder="How can we help you?"
                    className="min-h-[120px] mb-4"
                    autoFocus
                  />
                  <Button type="submit" disabled={sending || !supportMessage.trim()} className="w-full bg-zinc-900 hover:bg-zinc-800">
                    {sending ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Thread List */}
        {loadingThreads ? (
          <div className="text-center py-12 text-zinc-500">Loading...</div>
        ) : threads.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
            <h3 className="font-medium text-zinc-900 mb-2">No conversations yet</h3>
            <p className="text-sm text-zinc-500 mb-4">Contact support if you need help</p>
            <Button onClick={() => setShowNewSupport(true)} variant="outline">
              Contact Support
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {threads.map(thread => {
              const isSupport = thread.type === 'support';
              const isLocked = thread.status === 'locked';
              const hasUnread = (thread.unread_count || 0) > 0;
              
              return (
                <div
                  key={thread.id}
                  onClick={() => openThread(thread)}
                  className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:border-zinc-300 hover:shadow-sm ${
                    hasUnread ? 'border-zinc-900' : 'border-zinc-200'
                  }`}
                  data-testid={`thread-${thread.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isSupport ? 'bg-zinc-900 text-white' : isLocked ? 'bg-zinc-200 text-zinc-500' : 'bg-blue-100 text-blue-600'
                    }`}>
                      {isSupport ? <Headphones className="w-5 h-5" /> : isLocked ? <Lock className="w-5 h-5" /> : <Briefcase className="w-5 h-5" />}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-zinc-900 truncate">
                          {thread.display_name || (isSupport ? 'ZOLT Support' : 'Conversation')}
                        </h3>
                        {isSupport && (
                          <span className="px-2 py-0.5 bg-zinc-900 text-white text-xs rounded-full">Support</span>
                        )}
                        {isLocked && (
                          <span className="px-2 py-0.5 bg-zinc-200 text-zinc-600 text-xs rounded-full">Locked</span>
                        )}
                      </div>
                      <p className="text-sm text-zinc-500 truncate">{thread.last_message_preview || 'No messages yet'}</p>
                    </div>
                    
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-zinc-400">
                        {thread.last_message_at ? new Date(thread.last_message_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                      </p>
                      {hasUnread && (
                        <span className="inline-flex items-center justify-center w-5 h-5 bg-zinc-900 text-white text-xs rounded-full mt-1">
                          {thread.unread_count > 9 ? '9+' : thread.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardShell>
  );

  // ============================================================
  // THREAD VIEW (CONVERSATION)
  // ============================================================
  const ThreadView = () => {
    const canMessage = activeThread?.can_message !== false && activeThread?.status !== 'locked';
    const isSupport = activeThread?.type === 'support';
    const isLocked = activeThread?.status === 'locked' || !canMessage;

    return (
      <DashboardShell>
        <div className="max-w-3xl mx-auto">
          {/* Conversation Header */}
          <div className="bg-white rounded-xl border border-zinc-200 mb-4">
            <div className="px-4 py-3 flex items-center gap-3 border-b border-zinc-100">
              <button 
                onClick={() => navigate('/messages')} 
                className="text-zinc-600 hover:text-zinc-900 p-1"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isSupport ? 'bg-zinc-900 text-white' : 'bg-blue-100 text-blue-600'
              }`}>
                {isSupport ? <Headphones className="w-5 h-5" /> : isLocked ? <Lock className="w-5 h-5" /> : <Briefcase className="w-5 h-5" />}
              </div>
              
              <div className="flex-1 min-w-0">
                <h1 className="font-bold truncate">
                  {activeThread?.display_name || (isSupport ? 'ZOLT Support' : 'Conversation')}
                </h1>
                <p className="text-xs text-zinc-500">
                  {isSupport ? 'We\'re here to help' : isLocked ? 'Conversation locked' : 'Active conversation'}
                </p>
              </div>
            </div>

            {/* Privacy Notice */}
            <div className="bg-zinc-50 px-4 py-2 flex items-center gap-2 text-xs text-zinc-600">
              <Shield className="w-4 h-4" />
              <span>Messages are monitored for safety. Sharing contact info is not allowed.</span>
            </div>
          </div>

          {/* Messages Container */}
          <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden flex flex-col" style={{ height: '500px' }}>
            {/* Messages Area - Scrollable */}
            <div 
              ref={messagesContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto p-4 space-y-3"
            >
              {loadingMessages ? (
                <div className="text-center py-8 text-zinc-500">Loading messages...</div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                  <p className="text-zinc-500">No messages yet</p>
                  {!isLocked && <p className="text-sm text-zinc-400 mt-1">Start the conversation!</p>}
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isMe = msg.sender_id === user?.id;
                  const isSystemMsg = msg.sender_type === 'system';
                  const isSupportMsg = msg.sender_type === 'support';
                  
                  // System message
                  if (isSystemMsg) {
                    return (
                      <div key={msg.id || index} className="text-center py-2">
                        <span className="text-xs text-zinc-500 italic bg-zinc-100 px-3 py-1 rounded-full">
                          {msg.content}
                        </span>
                      </div>
                    );
                  }
                  
                  return (
                    <div key={msg.id || index} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className="max-w-[75%]">
                        {/* Sender name for others */}
                        {!isMe && (
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                              isSupportMsg ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-600'
                            }`}>
                              {isSupportMsg ? 'Z' : (msg.sender_name?.[0] || 'U')}
                            </div>
                            <span className="text-xs text-zinc-500">
                              {isSupportMsg ? 'ZOLT Support' : msg.sender_name}
                            </span>
                          </div>
                        )}
                        
                        {/* Message Bubble */}
                        <div className={`px-4 py-2.5 rounded-2xl ${
                          isMe 
                            ? `bg-zinc-900 text-white rounded-br-md ${msg._pending ? 'opacity-70' : ''}`
                            : 'bg-zinc-100 text-zinc-900 rounded-bl-md'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          
                          {msg.redacted && (
                            <div className="flex items-center gap-1 mt-1 text-xs opacity-60">
                              <Shield className="w-3 h-3" />
                              <span>edited</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Timestamp & Status */}
                        <div className={`flex items-center gap-1 mt-1 text-xs text-zinc-400 ${isMe ? 'justify-end' : ''}`}>
                          <span>
                            {new Date(msg.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {isMe && !msg._pending && (
                            msg.read_by?.length > 1 
                              ? <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
                              : <Check className="w-3.5 h-3.5" />
                          )}
                          {msg._pending && <Clock className="w-3.5 h-3.5 animate-pulse" />}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area - Fixed at bottom */}
            {!isLocked ? (
              <div className="border-t border-zinc-100 bg-white p-4">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-full text-sm focus:outline-none focus:border-zinc-400 focus:ring-1 focus:ring-zinc-400"
                    disabled={sending}
                    data-testid="message-input"
                    autoComplete="off"
                    dir="ltr"
                    style={{ direction: 'ltr', textAlign: 'left' }}
                  />
                  <Button 
                    type="submit" 
                    disabled={sending || !newMessage.trim()}
                    className="bg-zinc-900 hover:bg-zinc-800 rounded-full w-10 h-10 p-0 flex items-center justify-center"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            ) : (
              <div className="border-t border-zinc-100 bg-zinc-50 p-4 text-center">
                <p className="text-sm text-zinc-500 flex items-center justify-center gap-2">
                  <Lock className="w-4 h-4" />
                  This conversation is locked
                </p>
              </div>
            )}
          </div>
        </div>
      </DashboardShell>
    );
  };

  // Render based on view
  if (view === 'thread' && (threadId || jobId || activeThread)) {
    return <ThreadView />;
  }
  
  return <ThreadListView />;
};

export default MessagesPage;
